import React, { useState, useEffect, useRef } from "react";
import TransactionForm from "./TransactionForm";
// Import dei movimenti da un foglio .xlsx (cantiere FINANCE-IMPORT): il
// vaglio delle righe è nel modulo puro, l'anteprima con conferma digitata
// nella finestra; qui solo l'orchestrazione (file → lettura → anteprima →
// conferma → POST /transactions/import → ricarica).
import ImportTransactionsModal from "./ImportTransactionsModal";
import { normalizzaFoglio } from "../utils/importTransactions";
// Telaio comune (doc 07 §7): barra utente, banda con «‹ Back», colonna degli
// strumenti admin a tutta finestra sopra i 768px (§8.1) — come UsersList.
import HomeShell from "./HomeShell";
// Canale unico verso il backend: il client api.js — che antepone l'indirizzo
// del backend e allega il token dell'utente. Backup ed Export non chiamano più
// il backend (si producono nel browser), Reset All non esiste più: qui non
// resta alcuna chiamata axios.
import { api, TIMEOUT_LUNGO_MS } from "../api/api";
import "./ClubFinance.css";

const accounts = [
  { key: "bank1", label: "BOQ" },
  { key: "bank2", label: "CBA" },
  { key: "cash",   label: "CASH" },
];

function formatMoney(val) {
  if (val === "" || isNaN(val)) return "";
  const number = Number(val);
  const sign = number >= 0 ? "+" : "–";
  return (
    "$\u202f" +
    sign +
    Math.abs(number).toLocaleString("en-AU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}

function getAccountColor(val) {
  if (val > 0) return "clubfinance-green";
  if (val < 0) return "clubfinance-red";
  return "";
}

export default function ClubFinance() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [initialEdit, setInitialEdit] = useState(false);
  const [initialDraft, setInitialDraft] = useState({});
  const [editId, setEditId] = useState(null);        // ID transazione in edit
  const [editData, setEditData] = useState(null);    // Dati transazione edit
  // Anteprima dell'import: null = finestra chiusa; altrimenti il nome del
  // file e l'esito del vaglio ({ errore, valide, scartate }).
  const [importPreview, setImportPreview] = useState(null);
  const importInputRef = useRef(null);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const data = await api.get("/transactions");
      setTransactions(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, []);

  // Toolbar actions

  // Le righe da esportare: stesse colonne e stessi dati per Excel e CSV,
  // costruite una volta sola da ciò che è già a schermo.
  const buildExportRows = () => {
    const n = (v) => Number(String(v).replace(/[^0-9.-]/g, "")) || 0;

    return displayTrans.map((t, idx) => ({
      Date: formatDate(t.date),
      Client:
        typeof t.clientOrSupplier === "object"
          ? t.clientOrSupplier.companyName ||
            `${t.clientOrSupplier.firstName || ""} ${t.clientOrSupplier.lastName || ""}`.trim()
          : t.clientOrSupplier || "",
      Description: t.description || "",
      Category: typeof t.category === "object" ? t.category.name : t.category || "",
      BOQ: n(t.bank1),
      CBA: n(t.bank2),
      CASH: n(t.cash),
      BALANCE: displayBalances[idx],
    }));
  };

  // BACKUP — copia integrale in JSON, prodotta nel browser.
  // Non passa dal backend: serializza le transazioni così come sono arrivate
  // da GET /transactions (tutti i campi, nessuna trasformazione) e le scarica.
  const handleBackup = () => {
    setMsg({ type: "", text: "" });
    try {
      const contenuto = JSON.stringify(transactions, null, 2);
      const url = window.URL.createObjectURL(
        new Blob([contenuto], { type: "application/json" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `transactions_backup_${new Date().toISOString().slice(0, 10)}.json`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMsg({ type: "success", text: "Backup downloaded." });
    } catch (err) {
      console.error(err);
      setMsg({ type: "error", text: "Backup failed." });
    }
    setTimeout(() => setMsg({ type: "", text: "" }), 2300);
  };

  // EXPORT CSV — stesso meccanismo dell'Export Excel (foglio xlsx +
  // XLSX.writeFile): cambia solo l'estensione del file, che per la libreria
  // decide il formato di scrittura (csv invece di xlsx).
  const handleExport = async () => {
    setMsg({ type: "", text: "" });

    try {
      const XLSX = await import("xlsx");

      const worksheet = XLSX.utils.json_to_sheet(buildExportRows());
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Club Finance");

      const filename = `ClubFinance_${new Date().toISOString().slice(0,19).replace(/[-T:]/g, "")}.csv`;

      XLSX.writeFile(workbook, filename);

      setMsg({ type: "success", text: "Exported as CSV." });
    } catch (err) {
      console.error(err);
      setMsg({ type: "error", text: "Export failed." });
    }
    setTimeout(() => setMsg({ type: "", text: "" }), 2300);
  };

  // INIZIO  EXPORTA FILE EXCEL
  const handleExportExcel = async () => {
    setMsg({ type: "", text: "" });

    try {
      const XLSX = await import("xlsx");

      const worksheet = XLSX.utils.json_to_sheet(buildExportRows());
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Club Finance");

      const filename = `ClubFinance_${new Date().toISOString().slice(0,19).replace(/[-T:]/g, "")}.xlsx`;

      XLSX.writeFile(workbook, filename);

      setMsg({ type: "success", text: "Excel file exported successfully." });

    } catch (err) {
      console.error(err);
      setMsg({ type: "error", text: "Excel export failed." });
    }

    setTimeout(() => setMsg({ type: "", text: "" }), 2300);
  };

  // FINE  EXPORTA FILE EXCEL

  // IMPORT — movimenti da un foglio .xlsx, letto nel browser.
  // Il pulsante apre il selettore (l'<input type="file"> è nascosto);
  // scelto il file, lo si legge con lo stesso pattern di EventModal.jsx
  // (arrayBuffer → XLSX.read → primo foglio → sheet_to_json), si chiedono
  // al backend le due liste con cui risolvere i nomi (le stesse GET del
  // form: /contacts e /categories) e si apre l'anteprima. NESSUNA scrittura
  // avviene qui: solo dopo la conferma digitata, in handleImportConfirm.
  const handleImportClick = () => {
    if (importInputRef.current) importInputRef.current.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Si azzera subito, così lo stesso file si può riscegliere dopo.
    e.target.value = "";
    if (!file) return;
    setMsg({ type: "", text: "" });

    if (!/\.xlsx$/i.test(file.name)) {
      setMsg({ type: "error", text: "Only .xlsx files are accepted." });
      setTimeout(() => setMsg({ type: "", text: "" }), 2300);
      return;
    }

    try {
      const XLSX = await import("xlsx");
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const righe = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const [contacts, categories] = await Promise.all([
        api.get("/contacts"),
        api.get("/categories"),
      ]);

      const esito = normalizzaFoglio(righe, { contacts, categories });
      setImportPreview({ fileName: file.name, esito });
    } catch (err) {
      console.error(err);
      setMsg({ type: "error", text: "Could not read the file." });
      setTimeout(() => setMsg({ type: "", text: "" }), 2300);
    }
  };

  // La scrittura: UNA richiesta con le sole righe valide, alla rotta di
  // import in blocco (id coniati dal server, mai sovrascritture). Se lancia,
  // è la finestra a mostrare l'errore e a restare aperta.
  const handleImportConfirm = async (transactions) => {
    const esito = await api.post(
      "/transactions/import",
      { transactions },
      { timeoutMs: TIMEOUT_LUNGO_MS }
    );
    setImportPreview(null);
    const n = esito && typeof esito.count === "number" ? esito.count : transactions.length;
    setMsg({ type: "success", text: `Imported ${n} transaction${n === 1 ? "" : "s"}.` });
    setTimeout(() => setMsg({ type: "", text: "" }), 2300);
    loadTransactions();
  };

  const handleImportClose = () => setImportPreview(null);

  // Statistiche in alto
  const getBalance = (accountKey) =>
    transactions.reduce((sum, t) => sum + Number(t[accountKey] || 0), 0);
  const totalRaw = getBalance("bank1") + getBalance("bank2") + getBalance("cash");
  const cardStats = [
    {
      label: "BOQ Balance",
      value: formatMoney(getBalance("bank1")),
      colorClass: "clubfinance-blue"
    },
    {
      label: "CBA Balance",
      value: formatMoney(getBalance("bank2")),
      colorClass: "clubfinance-yellow"
    },
    {
      label: "Cash Balance",
      value: formatMoney(getBalance("cash")),
      colorClass: "clubfinance-white"
    },
    {
      label: "Total Balance",
      value: formatMoney(totalRaw),
      colorClass: totalRaw >= 0 ? "clubfinance-total-pos" : "clubfinance-total-neg"
    }
  ];

  // --- Add INITIAL BALANCE row (last)
  let initial = transactions.find(t => t.isInitialBalance);
  let others = transactions.filter(t => !t.isInitialBalance);

  // Ordina tutti dalla data più vecchia alla più recente
  let allTrans = [...others].sort((a, b) => new Date(a.date) - new Date(b.date));
  if (initial) allTrans.unshift(initial); // initial balance all'inizio per calcolo saldo

  // Calcola il saldo progressivo per OGNI riga (partendo dalla initial balance, dalla più vecchia)
  let balances = [];
  let running = 0;
  allTrans.forEach((t, idx) => {
    if (t.isInitialBalance) {
      running = Number(t.bank1 || 0) + Number(t.bank2 || 0) + Number(t.cash || 0);
    } else {
      running += Number(t.bank1 || 0) + Number(t.bank2 || 0) + Number(t.cash || 0);
    }
    balances.push(running);
  });

  // Ora mostriamo le transazioni più recenti in alto (reverse)
  let displayTrans = [...allTrans].reverse();
  let displayBalances = [...balances].reverse();

  // --- UTILS per rendering ---
  function renderCategory(cat) {
    if (!cat) return "";
    if (typeof cat === "object") return cat.name || "";
    return cat;
  }
  function renderClientOrSupplier(c) {
    if (!c) return "";
    if (typeof c === "object") {
      if (c.companyName) return c.companyName;
      const n = [c.firstName, c.lastName].filter(Boolean).join(" ");
      return n || "";
    }
    return c;
  }

  // Inline edit initial balance
  const onInitialEdit = (initRow) => {
    setInitialDraft({
      date: initRow.date,
      bank1: initRow.bank1,
      bank2: initRow.bank2,
      cash: initRow.cash,
    });
    setInitialEdit(true);
  };
  const onInitialCancel = () => setInitialEdit(false);

  const onInitialSave = async () => {
    setMsg({ type: "", text: "" });

    // ⚠️ Si scrive sul documento REALE della riga iniziale — lo stesso che ha
    // reso la riga (`initial`, l'unico con isInitialBalance: true). Prima si
    // mandava la stringa letterale "initial-balance" come id, e ogni salvataggio
    // creava un documento-fantasma con quel nome.
    const idIniziale = initial?.id;
    if (!idIniziale) {
      // Nessun documento iniziale: qui non se ne inventa uno.
      setMsg({ type: "error", text: "No INITIAL BALANCE record found. Nothing was saved." });
      setTimeout(() => setMsg({ type: "", text: "" }), 2300);
      return;
    }

    try {
      await api.patch(`/transactions/${idIniziale}`, {
      ...initialDraft,
      isInitialBalance: true
    });
      setMsg({ type: "success", text: "INITIAL BALANCE updated." });
      setInitialEdit(false);
      loadTransactions();
    } catch {
      setMsg({ type: "error", text: "Update failed." });
    }
    setTimeout(() => setMsg({ type: "", text: "" }), 2300);
  };

  // Start: EDIT transaction
  const onEdit = (t) => {
    setEditId(t.id);
    setEditData({
      ...t
    });
    window.scrollTo({ top: 0, behavior: "smooth" }); // Scorri in alto
  };
  const onEditCancel = () => {
    setEditId(null);
    setEditData(null);
  };
  
  // Delete transaction
  const onDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this transaction?")) return;
    setMsg({ type: "", text: "" });
    try {
      await api.del(`/transactions/${id}`);
      setMsg({ type: "success", text: "Transaction deleted." });
      loadTransactions();
      // Se stavi editando questa riga, resetta
      if (editId === id) onEditCancel();
    } catch {
      setMsg({ type: "error", text: "Delete failed." });
    }
    setTimeout(() => setMsg({ type: "", text: "" }), 2300);
  };

  // Salva modifica
  const onEditSave = async (data) => {
    setMsg({ type: "", text: "" });
    try {
      await api.patch(`/transactions/${editId}`, data);
      setMsg({ type: "success", text: "Transaction updated." });
      setEditId(null);
      setEditData(null);
      loadTransactions();
    } catch {
      setMsg({ type: "error", text: "Edit failed." });
    }
    setTimeout(() => setMsg({ type: "", text: "" }), 2300);
  };

  return (
    <HomeShell backTo="/home/superadmin" width="admin">
      {/* Telaio comune (doc 07 §7), come UsersList: il titolo è la classe
          condivisa (§3.1) ed è figlio diretto della colonna; «‹ Back» porta
          alla home SuperAdmin, come Season Management (altra pagina riservata
          al solo SUPERADMIN). Il riquadro qui sotto non fa più da pagina —
          larghezza e margini li dà la colonna degli strumenti admin (§8.1). */}
      <h1 className="qpsc-page-title">Club Finance</h1>
    <div className="clubfinance-table-container">
      {/* Toolbar */}
      <div className="clubfinance-toolbar" style={{ display: "flex", gap: 16, justifyContent: "flex-end", marginBottom: 12 }}>
        <button onClick={handleBackup} className="clubfinance-btn">Backup</button>
        <button onClick={handleExport} className="clubfinance-btn">Export CSV</button>
        <button onClick={handleExportExcel} className="clubfinance-btn">Export Excel</button>
        <button onClick={handleImportClick} className="clubfinance-btn">Import</button>
        <input
          ref={importInputRef}
          type="file"
          accept=".xlsx"
          style={{ display: "none" }}
          onChange={handleImportFile}
        />
      </div>
      {/* Montata solo con un'anteprima in mano: così la parola digitata e
          l'errore ripartono da zero a ogni file, non restano dal precedente. */}
      {importPreview && (
        <ImportTransactionsModal
          isOpen
          fileName={importPreview.fileName}
          esito={importPreview.esito}
          onConfirm={handleImportConfirm}
          onClose={handleImportClose}
        />
      )}
      {/* Messaggi */}
      {msg.text && (
        <div className={`clubfinance-msg clubfinance-msg-${msg.type}`}>
          {msg.text}
        </div>
      )}

      {/* Statistiche */}
      <div className="clubfinance-stat-cards">
        {cardStats.map((stat) => (
          <div className={`clubfinance-card ${stat.colorClass} ${stat.label === "Total Balance" ? "clubfinance-card-total" : ""}`} key={stat.label}>
            <div className="clubfinance-card-title">{stat.label}</div>
            <div className={`clubfinance-card-value ${stat.colorClass}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      <TransactionForm
        onSuccess={loadTransactions}
        accounts={accounts}
        formatMoney={formatMoney}
        editId={editId}
        editData={editData}
        onEditSave={onEditSave}
        onEditCancel={onEditCancel}
      />

      {loading ? (
        <div>Loading...</div>
      ) : (
        <table className="clubfinance-table">
          <thead>
            <tr>
              <th className="col-date">Date</th>
              <th className="col-client">Client</th>
              <th className="col-description">Description</th>
              <th className="col-category">Cat.</th>
              <th className="col-amount">BOQ</th>
              <th className="col-amount">CBA</th>
              <th className="col-amount">CASH</th>
              <th className="col-balance">BAL</th>
              <th className="col-actions" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayTrans.map((t, idx) => {
              // INITIAL BALANCE ROW
              if (t.isInitialBalance) {
                return (
                  <tr key={t.id || "initial"} className="clubfinance-initial-row">
                    <td className="col-date">
                      {initialEdit ? (
                        <input
                          type="date"
                          value={initialDraft.date}
                          onChange={e =>
                            setInitialDraft(d => ({ ...d, date: e.target.value }))
                          }
                        />
                      ) : (
                        formatDate(t.date)
                      )}
                    </td>
                    <td colSpan={2} style={{ fontWeight: 600, color: "#b0e3fc" }}>INITIAL BALANCE</td>
                    <td className="col-category"></td>
                    <td>{initialEdit ? (
                        <input
                          type="number"
                          value={initialDraft.bank1}
                          onChange={e =>
                            setInitialDraft(d => ({ ...d, bank1: e.target.value }))
                          }
                          style={{ width: 80 }}
                        />
                      ) : (
                        ""
                      )}
                    </td>
                    <td>{initialEdit ? (
                        <input
                          type="number"
                          value={initialDraft.bank2}
                          onChange={e =>
                            setInitialDraft(d => ({ ...d, bank2: e.target.value }))
                          }
                          style={{ width: 80 }}
                        />
                      ) : (
                        ""
                      )}
                    </td>
                    <td>{initialEdit ? (
                        <input
                          type="number"
                          value={initialDraft.cash}
                          onChange={e =>
                            setInitialDraft(d => ({ ...d, cash: e.target.value }))
                          }
                          style={{ width: 80 }}
                        />
                      ) : (
                        ""
                      )}
                    </td>
                    <td style={{ fontWeight: 700 }}>
                      {formatMoney(displayBalances[idx])}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {initialEdit ? (
                        <>
                          <button className="clubfinance-btn" onClick={onInitialSave}>
                            Save
                          </button>
                          <button className="clubfinance-btn" onClick={onInitialCancel}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button className="clubfinance-btn" onClick={() => onInitialEdit(t)}>
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              }
              // Transazione normale
              return (
                <tr key={t.id || idx}>
                  <td className="col-date">{formatDate(t.date)}</td>
                  <td className="col-client">{renderClientOrSupplier(t.clientOrSupplier)}</td>
                  <td className="col-description">{t.description}</td>
                  <td className="col-category">{renderCategory(t.category)}</td>
                  {accounts.map((acc) => {
                    const val = Number(t[acc.key] || 0);
                    return (
                      <td key={acc.key} className={`col-amount ${getAccountColor(val)}`}>
                        {val !== 0 ? formatMoney(val) : ""}
                      </td>
                    );
                  })}
                  <td
                    className={`col-balance ${
                      displayBalances[idx] < 0
                        ? "clubfinance-red"
                        : displayBalances[idx] > 0
                        ? "clubfinance-green"
                        : ""
                    }`}
                  >
                    {formatMoney(displayBalances[idx])}
                  </td>
                  <td className="col-actions" style={{ textAlign: "right" }}>
                    <button
                      title="Edit"
                      className="clubfinance-icon-btn"
                      onClick={() => onEdit(t)}
                      style={{ marginRight: 4 }}
                    >
                      ✏️
                    </button>

                    <button
                      title="Delete"
                      className="clubfinance-icon-btn"
                      onClick={() => onDelete(t.id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
    </HomeShell>
  );
}